<template>
  <v-app>
    <v-toolbar app v-show="showToolbar">
      <!-- <v-toolbar-title class="headline text-uppercase">
        <span>Stopwatch <v-icon>timer</v-icon></span>
      </v-toolbar-title> -->
      <v-select id="project-select" solo 
        class="project-select disablehotkeys" 
        :items="projects"
        v-model="selectedProjectId"
        :item-text="i => i.name ? i.name : 'Project ' + i.id"
        item-value="id"
        required
      ></v-select>
      <v-btn flat target="_blank" class="btn-projects"
        v-shortkey="['q']" @shortkey="projectShortkey"
        @click="manageProjects">
        <v-icon>folder</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn flat target="_blank" @click="openCharts" 
        v-shortkey="['s']" @shortkey="openCharts">
        <v-icon>pie_chart</v-icon>
      </v-btn>
      <!-- <v-btn flat target="_blank"
        @click="toggleToolbar"
        @shortkey="toggleToolbar" 
        v-shortkey="['ctrl','q']" >
        <v-icon>visibility_off</v-icon>
      </v-btn> -->
      <v-btn flat target="_blank"
        @click="openHelp" >
        <v-icon>help</v-icon>
      </v-btn>
      <v-btn flat target="_blank"
        @click="openSettingsDialog" >
        <v-icon>settings</v-icon>
      </v-btn>
    </v-toolbar>

    <v-content>
      <div class="status-border" 
        v-if="currentProject.state !== 'reset'"
        :class="{ 
          '--running': showRunningIndicator,
          '--pause': showPauseIndicator,
          paused: currentProject.state === 'paused' 
        }">
      </div>

      <v-container v-if="currentProject">
        <v-layout text-xs-center wrap grow xs12 mt-3>
          <v-flex xs12 class="stopwatch-icon" @click="toggleTimerType('TASK')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="svg-icon">
              <path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
            </svg>
          </v-flex>

          <!-- Stopwatch Section -->
          <v-flex mb-4 class="watch-buttons" v-if="currentSession">
            <div class="clock">
              <h1 class="font-weight-bold unselectable" @click="toggleTimerType()" v-html="displayTime" unselectable="on"></h1>
              <template v-if="currentProject.state !== 'reset'">
                <p class="sub-text" v-if="timerType === 'PROJECT'" :title="'Project started: ' + currentProject.startDateFormat">
                  Total project time
                </p>
                <p class="sub-text" v-if="timerType === 'SESSION'">
                  Current session time
                </p>
                <p class="sub-text" v-if="timerType === 'TASK'">
                  Current task time
                </p>
                <p class="sub-text" v-if="timerType === 'PAUSE' && currentProject.state === 'started'">
                  Time since last paused
                </p>
                <p class="sub-text" v-if="timerType === 'PAUSE' && currentProject.state !== 'started'">
                  Current pause time
                </p>
              </template>
            </div>

            <!-- v-shortkey="['s']" -->
            <v-btn v-if="currentProject.state === 'reset'" @click="start" color="success" @shortkey="start" v-shortkey="['enter']">
              <v-icon>play_arrow</v-icon>Start session
            </v-btn>

            <v-btn v-if="currentProject.state === 'paused'" @click="resume" @shortkey="resume" v-shortkey="['space']"><v-icon>play_arrow</v-icon>Resume</v-btn>
            <v-btn v-if="currentProject.state === 'started'" @click="pause" @shortkey="pause" v-shortkey="['space']" class="break-btn"><v-icon>pause</v-icon>Break</v-btn>
            <v-btn v-if="currentProject.state === 'started'" @click="addMinutes(-5)" color="#263238" class="mr-1">- 5 min</v-btn>
            <v-btn v-if="currentProject.state === 'started'" @click="addMinutes(5)" color="#263238">+ 5 min</v-btn>

            <!-- v-shortkey="['ctrl', 'r']" -->
            <v-btn v-if="currentProject.state === 'paused'" @click="endSession" color="warning" @shortkey="endSession" v-shortkey="['r']">
              <v-icon>rotate_right</v-icon>End session
            </v-btn>
            <v-btn v-if="currentProject.state !== 'started' && currentProject.sessions.length > 1" @click="deleteSession" 
                @shortkey="deleteSession" v-shortkey="['r']" class="px-0" title="Go back to previous session">
              <v-icon>rotate_left</v-icon>
            </v-btn>

            <v-btn v-if="currentProject.state !== 'reset'" @click="saveTask()" color="primary" v-shortkey="['enter']" @shortkey="saveTask()">
              <v-icon>playlist_add</v-icon>New Task
            </v-btn>
          </v-flex>

          <v-flex xs12 v-if="currentSession && currentProject.state !== 'reset'">
            <a href="#" class="objectives-button" @click.prevent="showSessionNotesDialog">Objectives for this session</a>
            <p class="session-objectives" v-html="newLine2br(currentSession.objectives)"></p>
            <v-progress-linear 
              v-if="currentSession.targetTime"
              :value="targetTimePercent"
              class="session-progress"
              color="lightblue"
              height="15"
            ></v-progress-linear>
          </v-flex>

          <!-- Task Section -->
          <v-flex v-if="sessionTasks.length > 0" xs12 class="tasks-content">
            <v-layout justify-center>
              <template>
                <v-data-table class="tasks-table"
                  :headers="taskHeaders"
                  :pagination.sync="tasksPagination"
                  :items="sessionTasks"
                  hide-actions disable-pagination>

                  <template v-slot:items="props">
                    <tr :class="{ current: (props.item.id == currentProject.currentTaskId), complete: props.item.isComplete }">
                      <td class="text-xs-left id-column">{{ indexTask(props.item) }}</td>
                      <td class="text-xs-left complete-checkbox">
                        <!-- color="success" -->
                        <v-checkbox color="#797979" 
                          v-model="props.item.isComplete" 
                          @change="storeInstance"
                        ></v-checkbox>
                      </td>
                      <td class="text-xs-left">{{ formatTime(getLapsedTimeTask(props.item)) }}</td>
                      <!-- <td class="text-xs-left">{{ props.item.start }}</td>
                      <td class="text-xs-left">{{ props.item.stop }}</td>
                      <td class="text-xs-left">{{ formatTime(props.item.previousLapsed) }}</td> -->
                      <td class="text-xs-left" style="width: 100%">
                        <v-text-field v-model="props.item.name" v-on:blur="storeInstance" height="20" class="disablehotkeys task-name"
                          :id="'input-'+props.item.id" autocomplete="off" @keyup.enter.native="removeFocus"></v-text-field>
                      </td>
                      <td class="justify-center layout px-0 actions-column">
                        <v-icon @click="setCurrentTask(props.item)" class="mr-2">play_arrow</v-icon>
                        <v-icon @click="removeTask(props.item)">delete</v-icon>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </template>
            </v-layout>
            <div class="sub-text">
              Session started: {{ formatDate(currentSession.startTime) }}
            </div>
          </v-flex>
        </v-layout>
      </v-container>

      <!-- Session-Notes-Dialog -->
      <v-dialog v-model="sessionNotesDialog" v-if="currentProject && currentSession" max-width="800px">
        <v-card id="charts-dialog" class="disablehotkeys" style="overflow:visible;">
          <v-card-title>
            <span class="headline" v-if="currentSession.startTime == null">Start session for {{ getProjectName(currentProject) }}</span>
            <span class="headline" v-else>Session objectives for {{ currentProject.name }}</span>
            <v-spacer></v-spacer>
            <v-btn @click="sessionNotesDialog=false" icon><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md pb-0>
              <v-layout wrap pb-0>
                <v-flex xs12>
                  <label>What are your objectives for this session?</label>
                  <v-textarea solo
                    :rows="4" auto-grow
                    id="session-objectives"
                    v-model="currentSession.objectives"
                    v-on:blur="storeInstance"
                  ></v-textarea>

                  <label>How many hours do you expect to work?</label>
                  <v-text-field solo
                    label="Solo"
                    type="number"
                    v-model="currentSession.targetTime"
                    placeholder="Number of hours"
                    v-on:blur="storeInstance"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions class="pt-0">
            <v-btn color="blue darken-1" @click="confirmStart" v-if="currentSession.startTime == null">Start session</v-btn>
            <v-btn color="blue darken-1" @click="sessionNotesDialog=false" v-else>Save session</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Session-End-Dialog -->
      <v-dialog v-model="sessionEndDialog" v-if="currentProject && currentSession" max-width="800px">
        <v-card id="charts-dialog" class="disablehotkeys" style="overflow:visible;">
          <v-card-title>
            <span class="headline">End session for {{ getProjectName(currentProject) }}</span>
            <v-spacer></v-spacer>
            <v-btn @click="sessionEndDialog=false" icon><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md pb-0>
              <v-layout wrap pb-0>
                <v-flex xs12>
                  <v-textarea outline :disabled="true"
                    :value="currentSession.objectives"
                    label="Objectives for this session"
                  ></v-textarea>

                  <label class="mb-4">How well did you do from 0 to 10?</label>
                  <v-card-text>
                    <v-slider
                      v-model="currentSession.rating"
                      step="1" max="10"
                      ticks="always"
                      tick-size="2"
                      thumb-label="always"
                      thumb-color="#3b4044"
                    ></v-slider>
                  </v-card-text>

                  <label>How was your performance in this session?</label>
                  <v-textarea solo auto-grow
                    id="session-end-notes"
                    v-model="currentSession.endNotes"
                  ></v-textarea>

                  <label>Objectives for next session? (optional)</label>
                  <v-textarea solo auto-grow
                    id="session-end-notes"
                    v-model="currentProject.nextObjectives"
                  ></v-textarea>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions  class="pt-0">
            <v-btn color="blue darken-1" @click="confirmEndSession">End session</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Stats-Dialog -->
      <StatsModal ref="statsModal" v-bind:currentProject="currentProject"></StatsModal>

      <!-- Manage Projects - Dialog -->
      <v-dialog v-model="projectsDialog" max-width="600px">
        <v-card class="disablehotkeys">
          <v-card-title>
            <span class="headline">Manage projects</span>
            <v-spacer></v-spacer>
            <v-btn @click="projectsDialog = false" icon><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-layout wrap>
                <v-flex xs12>
                  <v-data-table
                    :headers="projectsHeaders"
                    :pagination.sync="projectsPagination"
                    :items="projects"
                    hide-actions disable-pagination>

                    <template v-slot:items="props">
                      <tr :class="{ current: (props.item.id == currentProjectId) }">
                        <td class="text-xs-left" style="width: 100%">
                          <v-text-field v-model="props.item.name" @change="storeInstance" height="20" 
                            @keyup.enter.native="projectsDialog=false,selectedProjectId=props.item.id"
                            :placeholder="'Project ' + props.item.id" :id="'input-project-'+props.item.id" autocomplete="off">
                          </v-text-field>
                        </td>
                        <td class="justify-center layout"> 
                          <v-icon @click="resetProject(props.item)" class="mr-3">rotate_right</v-icon>
                          <v-icon @click="removeProject(props.item)">delete</v-icon>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-flex>
                <v-btn color="blue darken-1 new-project-btn" @click="createNewProject">
                  <v-icon>playlist_add</v-icon>New project
                </v-btn>
              </v-layout>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Settings Dialog -->
      <v-dialog v-model="settingsDialog" max-width="600px">
        <v-card class="disablehotkeys">
          <v-card-title>
            <span class="headline">User Settings</span>
            <v-spacer></v-spacer>
            <v-btn @click="settingsDialog = false" flat icon><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12>
                  <v-checkbox v-model="showTimerTitle" label="Include timer in title"
                    messages="Shows a live timer in the window title.">
                  </v-checkbox>
                  <v-checkbox v-model="showRunningIndicator" label="Tracking indicator"
                    messages="Displays green border around timer to indicate tracking.">
                  </v-checkbox>
                  <v-checkbox v-model="showPauseIndicator" label="Pause indicator"
                    messages="Displays a flashing border around timer to indicate pause.">
                  </v-checkbox>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn color="blue darken-1 mr-2" @click="saveSettings">Save settings</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Help-Dialog -->
      <v-dialog v-model="helpDialog" max-width="600px">
        <v-card class="disablehotkeys">
          <v-card-title>
            <span class="headline">Stopwatch Help</span>
            <v-spacer></v-spacer>
            <v-btn @click="helpDialog = false" icon><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12>
                  <h3>Keyboard Shortcuts</h3>
                  Pause / Resume: <v-chip>SPACE</v-chip><br />
                  Add task / Start: <v-chip>ENTER</v-chip><br />
                  Switch project: <v-chip>q</v-chip><br />
                  New session: <v-chip>r</v-chip><br />
                  Show stats: <v-chip>s</v-chip><br />
                  <!-- Toggle toolbar: <v-chip>CTRL + q</v-chip><br /> -->
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Settings deleted snackbar -->
      <v-snackbar v-model="settingsDeletedSnackbar" bottom :timeout="20000">
        Settings deleted! Default settings will be applied on page-reload...
        <v-btn color="blue" flat @click="settingsDeletedSnackbar = false">
          Close
        </v-btn>
      </v-snackbar>
    </v-content>
  </v-app>
</template>

<script>
  import StatsModal from './components/StatsModal';
  import timeHelpers from './timeHelpers';

  const clone = require('clone');

  const INTERVAL_UPDATE = 1000;
  const PROJECTS_KEY = 'instance-projects';
  const CURRENT_PROJECT_ID_KEY = 'instance-currentProjectId';
  const PROJECT_KEY_COUNTER_KEY = 'instance-projectKeyCounter';
  const TIMER_TYPE_KEY = 'instance-timerType';

  const DEFAULT_SESSION = {
    startTime: null,
    endTime: null,
    objectives: "",
    targetTime: null,
    endNotes: "",
    rating: null,
    pauses: [],
    pausedTime: 0,
    previousLapsed: 0,
  };

  const DEFAULT_PROJECT = {
    name: '',
    state: 'reset',
    sessions: [ {...clone(DEFAULT_SESSION), id: 1} ],
    currentSessionId: 1,
    sessionKeyCounter: 2,
    nextObjectives: '',
    tasks: [],
    currentTaskId: null,
    taskKeyCounter: 1,
    showTotal: false,
  };

  const TIMER_TYPES = [
    "TASK", "PAUSE", "SESSION", "PROJECT"
  ];

  export default {
    components: {
      StatsModal
    },
    data: () => ({
      pageTitle: 'Trackier Work',
      pageSymbol: '',
      showToolbar: true,

      timerType: TIMER_TYPES[0],

      projects: [],
      selectedProjectId: null,
      currentProjectId: null,
      projectKeyCounter: 1,

      // time
      currentTime: Date.now(),
      intervalUpdate: null,
      intervalUpdateTime: 10,

      // table settings
      taskHeaders: [
        { text: '#', value: 'id', class: "id-column" },
        { text: 'Done', sortable: false },
        { text: 'Time spent', sortable: false },
        // { text: 'Start time', value: 'start', sortable: false },
        // { text: 'Stop time', value: 'stop', sortable: false },
        // { text: 'Previous', value: 'previousLapsed', sortable: false },
        { text: 'Task name', value: 'name', sortable: false },
        { text: 'Actions', value: 'id', sortable: false }
      ],
      tasksPagination: {
        sortBy: 'id',
        descending: true,
        rowsPerPage: -1
      },
      projectsHeaders: [
        { text: 'Project name', value: 'name', sortable: false },
        { text: 'Actions', value: 'id', sortable: false }
      ],
      projectsPagination: {
        sortBy: 'id',
        rowsPerPage: -1
      },

      // general settings
      showTimerTitle: false,
      showRunningIndicator: false,
      showPauseIndicator: false,

      // dialog and modal state
      sessionNotesDialog: false,
      sessionEndDialog: false,
      projectsDialog: false,
      settingsDialog: false,
      helpDialog: false,
      storedInstanceDetectedModal: false,
      settingsDeletedSnackbar: false,
    }),
    created() {
      this.startUpdateTask();
    },
    mounted() {
      const self = this;
      this.loadSettings();
      this.restoreStoredInstance();

      if (this.projects.length <= 0) {
        this.createNewProject();
      }

      window.addEventListener('storage', function(e) {  
        if ([PROJECTS_KEY, CURRENT_PROJECT_ID_KEY, 
            PROJECT_KEY_COUNTER_KEY, TIMER_TYPE_KEY].includes(e.key)) {
          self.restoreStoredInstance();
        }
      });
    },
    computed: {
      displayTime() {
        let t = 0;
        let project = this.currentProject;
        if (this.currentProject) {
          if (this.timerType == "SESSION") {
            t = this.lapsedMillisecondsSession;
          } else if (this.timerType == "PROJECT") {
            t = this.lapsedMillisecondsTotal
          } else if (this.timerType == "TASK") {
            let task = this.currentTask;
            if (task) t = this.getLapsedTimeTask(task);
            else t = null;
          } else if (this.timerType == "PAUSE") {
            let lastPause;
            let session = this.currentSession;
            let pauses = session.pauses;
            if (pauses.length > 0) lastPause = pauses[pauses.length-1];
            if (project.state == "started") {
              if (lastPause) {
                t = this.currentTime - lastPause.pauseEnd;
              } else {
                t = this.lapsedMillisecondsSession;
              }
            } else if (project.state == "paused") {
              t = this.currentTime - lastPause.pauseStart;
            }
          }
        }
        return this.formatTime(t);
      },
      lapsedMillisecondsSession() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return 0;
        else if (project.state == "reset") return 0;
        else if (project.state == "paused") {
          return session.previousLapsed || 0;
        } else {
          return this.currentTime - session.startTime - session.pausedTime;
        }
      },
      lapsedMillisecondsTotal() {
        let project = this.currentProject;
        if (!project) return 0;
        let totalMilliseconds = this.lapsedMillisecondsSession;
        project.sessions.forEach(s => {
          if (s.id != project.currentSessionId) {
            totalMilliseconds += s.previousLapsed;
          }
        });
        return totalMilliseconds;
      },
      currentProject() {
        if (!this.currentProjectId) {
          return null;
        } else {
          return this.projects.find(t => t.id == this.currentProjectId);
        }
      },
      currentTask() {
        let project = this.currentProject;
        if (!project || !project.currentTaskId) {
          return null;
        } else {
          return project.tasks.find(t => (
            t.id == project.currentTaskId && 
            t.sessionId == project.currentSessionId ));
        }
      },
      currentSession() {
        let project = this.currentProject;
        if (!project || !project.currentSessionId) {
          return null;
        } else {
          return project.sessions.find(s => s.id == project.currentSessionId);
        }
      },
      sessionTasks() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) {
          return [];
        } else {
          return project.tasks.filter(t => t.sessionId == project.currentSessionId);
        }
      },
      targetTimePercent() {
        let percent = 0;
        let project = this.currentProject;
        let session = this.currentSession;
        if (project && session && session.targetTime) {
          let target = session.targetTime;
          if (isNaN(target)) target = session.targetTime = parseFloat(target);
          if (!isNaN(target)) {
            percent = (this.lapsedMillisecondsSession * 100) / (target * 3600000);
          }
        }
        return percent;
      },
    },
    watch: {
      selectedProjectId(val) {
        var self = this;
        let currentProject = this.currentProject;
        let newProject = self.projects.find(t => t.id == val);
        if (newProject && currentProject && newProject.id != this.currentProjectId && 
            currentProject.state == 'started') {
          this.pause(); 
        }
        if (newProject.sessions.length > 0) {
          newProject.startDateFormat = this.formatDate(newProject.sessions[0].startTime);
        }
        self.currentProjectId = val;
        self.removeFocus();
        self.storeInstance();
      },
      currentProject: {
        deep: true,
        handler(p) {
          // var icon = document.getElementById('favicon');
          this.pageSymbol = '';
          if (p.state == "started") {
            this.pageSymbol = "🔴 "; // ▶️
            // icon.href = "./images/favicon-play.png"
          } else if (p.state == "paused") {
            this.pageSymbol = "❚❚ ";
            // icon.href = "./images/favicon.png"
          } else {
            // icon.href = "./images/favicon.png"
          }
          this.pageTitle = p.name;
        }
      },
      displayTime() { this.updateTitle(); },
      pageTitle() { this.updateTitle(); },
      pageSymbol() { this.updateTitle(); },
      showTimerTitle() { this.updateTitle(); },
    },
    methods: {
      ...timeHelpers,

      updateTitle() {
        let time;
        if (this.showTimerTitle && 
          this.currentProject && this.currentProject.state != 'reset') time = this.displayTime;
        window.document.title = this.pageSymbol + ((time) ? `(${time}) ` : '') +
             (this.pageTitle ? this.pageTitle :'Trackier Work');
      },

      openSettings() {
        this.openSettingsDialog();
      },
      openHelp() {
        this.helpDialog = true;
      },
      openCharts() {
        this.$refs.statsModal.updateChartSeries();
        this.$refs.statsModal.openDialog();
      },
      manageProjects() {
        this.projectsDialog = true;
      },
      openSettingsDialog() {
        this.settingsDialog = true;
      },
      toggleToolbar() {
        this.showToolbar = !this.showToolbar;
      },
      projectShortkey(){
        this.showToolbar = true;
        this.removeFocus();
        var select = document.getElementById('project-select');
        select.focus(); select.click();
        select.dispatchEvent(new KeyboardEvent("keydown", {
          keyCode: 40
        }));
      },
      removeFocus(){
        document.activeElement.blur();
      },
      
      toggleTimerType(type) {
        if (type) {
          this.timerType = type;
        } else {
          let newTypeIndex = TIMER_TYPES.indexOf(this.timerType) +1;
          if (newTypeIndex >= TIMER_TYPES.length) newTypeIndex = 0;
          this.timerType = TIMER_TYPES[newTypeIndex];
        }

        this.storeInstance();
      },

      createNewProject() {
        let projectId = this.projectKeyCounter++;
        let newProject = { ...clone(DEFAULT_PROJECT), id: projectId };

        this.projects.push(newProject);
        this.selectedProjectId = projectId;

        if (this.projectsDialog) {
          setTimeout(function() {
            document.getElementById('input-project-'+projectId).focus();
          }, 300);
        }

        this.storeInstance();
      },
      removeProject(project) {
        if (confirm(`Delete project "${project.name}" ?`)) {
          const index = this.projects.indexOf(project);
          if (this.projects.length <= 1) this.createNewProject();
          else this.selectedProjectId = this.projects[0].id;
          this.projects.splice(index, 1);
          this.storeInstance();
        }
      },
      resetProject: function(project) {
        if (!project) return;

        if (confirm(`Are you sure? The project "${project.name}" will be reset.`)) {
          let setDefaults = { ...clone(DEFAULT_PROJECT), 
            id: project.id,
            name: project.name 
          };
          for (var key in setDefaults) {
            project[key] = setDefaults[key];
          }

          this.storeInstance();
        }
      },
      getProjectName(project) {
        let name = project.name;
        return (name) ? name : "Project " + project.id;
      },

      start() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;
        this.showSessionNotesDialog();
      },
      confirmStart() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;
        this.sessionNotesDialog = false;
        project.state = 'started';
        session.startTime = Date.now();
        this.currentTime = session.startTime;
        this.saveTask(0);
        this.storeInstance();
      },
      pause: function() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;

        session.previousLapsed = this.lapsedMillisecondsSession;
        project.state = "paused";

        session.pauses.push({
          pauseStart: Date.now(),
          pauseEnd: null,
          pauseLength: null
        });

        let currentTask = this.currentTask;
        if (currentTask) currentTask.stop = session.previousLapsed;

        this.storeInstance();
      },
      resume: function() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;

        const end = Date.now();
        const start = session.pauses[session.pauses.length - 1].pauseStart;
        const length = end - start;

        session.previousLapsed = null;
        session.pausedTime += length;
        session.pauses[session.pauses.length - 1].pauseEnd = end;
        session.pauses[session.pauses.length - 1].pauseLength = length;
        project.state = "started";

        let currentTask = this.currentTask;
        if (currentTask) currentTask.stop = null;

        this.storeInstance();
      },
      endSession() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;

        this.sessionEndDialog = true;
        setTimeout(function() {
          var select = document.getElementById('session-end-notes');
          select.focus();
        }, 200);
      },
      confirmEndSession() {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;

        this.sessionEndDialog = false;
        project.state = "reset";
        session.endTime = Date.now();

        let endSessionId = project.sessionKeyCounter++;
        project.sessions.push({...DEFAULT_SESSION,
          id: endSessionId,
          objectives: project.nextObjectives
        });

        project.nextObjectives = ""

        project.currentSessionId = endSessionId;
        this.storeInstance();
      },
      deleteSession() {
        let project = this.currentProject;
        let sessionDel = this.currentSession;
        if (!project || !sessionDel) return;
        if (!project.sessions.length > 1) return;

        let msg = (project.state == "reset") ? "Go back to previous session?" :
          "Delete current session and go back to previous session?";

        if (confirm(msg)) {
          this.pause();
          project.state = "paused";
          project.sessionKeyCounter--;

          // Move current tasks to last session
          let lastSession = project.sessions[project.sessions.length - 2];
          project.tasks.forEach(t => {
            if (t.sessionId == sessionDel.id) {
              t.sessionId = lastSession.id,
              t.start += lastSession.previousLapsed;
              t.stop += lastSession.previousLapsed;
            }
          });

          lastSession.startTime -= sessionDel.previousLapsed;
          lastSession.previousLapsed += sessionDel.previousLapsed;

          project.currentSessionId = lastSession.id;

          const index = project.sessions.indexOf(sessionDel);
          project.sessions.splice(index, 1);

          this.storeInstance();
        }
      },

      updateCurrentTime() {
        if (this.intervalUpdate) {
          this.currentTime = Date.now();
        }
      },
      /** Start updating of current time */
      startUpdateTask() {
        if (!this.intervalUpdate) {
          this.intervalUpdate = setInterval(this.updateCurrentTime, INTERVAL_UPDATE);
        }
      },

      /** Save new task (with current time) */
      saveTask(start) {
        let project = this.currentProject;
        let session = this.currentSession;
        if (!project || !session) return;

        let currentTask = this.currentTask;
        if (currentTask) currentTask.stop = this.lapsedMillisecondsSession;

        if (!start && start != 0) start = this.lapsedMillisecondsSession;
        let id = project.taskKeyCounter++;
        let task = {
          id: id,
          sessionId: session.id,
          previousLapsed: 0,
          isComplete: false,
          start: start,
          stop: null,
          name: ''
        };

        project.currentTaskId = task.id;
        project.tasks.push(task);

        setTimeout(function() {
          document.getElementById('input-'+id).focus();
        }, 300);

        if (project.state == 'reset') this.start();
        if (project.state == 'paused') this.resume();

        this.storeInstance();
      },
      /** Remove specific task from tasks */
      removeTask(element) {
        if (confirm("Delete task?")) {
          let project = this.currentProject;
          if (!project) return;
          const index = project.tasks.indexOf(element);
          project.tasks.splice(index, 1);

          this.storeInstance();
        }
      },
      /** Switches the current task */
      setCurrentTask(element) {
        let project = this.currentProject;
        if (!project) return;
        let currentTask = this.currentTask;
        if (currentTask) currentTask.stop = this.lapsedMillisecondsSession;
        element.previousLapsed = this.getLapsedTimeTask(element);
        element.start = this.lapsedMillisecondsSession;
        element.stop = null;
        element.isComplete = false;
        project.currentTaskId = element.id;
        if (project.state != 'started') this.resume();
        this.storeInstance();
      },
      getLapsedTimeTask(element) {
        let project = this.currentProject;
        if (!project) return 0;
        var lapsed = (element.id == project.currentTaskId ? 
          (this.lapsedMillisecondsSession - element.start) : 
          (element.stop - element.start)) + (element.previousLapsed || 0);
        if (lapsed < 0) lapsed = 0;
        return lapsed;
      },
      indexTask(element) {
        let sessionTasks = this.sessionTasks;
        if (!sessionTasks) return 0;
        return sessionTasks.indexOf(element) + 1;
      },


      addMinutes(minutes) {
        let session = this.currentSession;
        if (!session) return;

        var currentTime = Date.now();
        var paused = session.pausedTime;
        var startTime = parseInt(session.startTime);
        var newTime = startTime - minutes*60*1000;
        if ((currentTime - newTime - paused) < 0) {
          newTime = currentTime - paused;
        }

        session.startTime = newTime;
        this.storeInstance();
      },

      showSessionNotesDialog() {
        this.sessionNotesDialog = true;
        setTimeout(function() {
          var select = document.getElementById('session-objectives');
          select.focus();
        }, 200);
      },

      /** Save current settings-state to the local storage */
      saveSettings() {
        localStorage.setItem('settings', JSON.stringify({
          showTimerTitle: this.showTimerTitle,
          showRunningIndicator: this.showRunningIndicator,
          showPauseIndicator: this.showPauseIndicator,
        }));

        this.settingsDialog = false;
      },
      /** Load current settings state from the local storage */
      loadSettings() {
        // Check, if settings have been saved in Local Storage
        let settings = localStorage.getItem('settings');
        if (settings === null || settings === undefined) return;
        settings = JSON.parse(settings);
        this.showTimerTitle = settings.showTimerTitle;
        this.showRunningIndicator = settings.showRunningIndicator;
        this.showPauseIndicator = settings.showPauseIndicator;
      },

      /**
       * Store the current state of the instance.
       */
      storeInstance() {
        localStorage.setItem(PROJECTS_KEY, JSON.stringify(this.projects));
        localStorage.setItem(CURRENT_PROJECT_ID_KEY, this.currentProjectId);
        localStorage.setItem(PROJECT_KEY_COUNTER_KEY, this.projectKeyCounter);
        localStorage.setItem(TIMER_TYPE_KEY, this.timerType);
      },
      /**
       * Restore the stored instance and start the stopwatch with the old parameters again.
       */
      restoreStoredInstance() {
        const instanceProjects = localStorage.getItem(PROJECTS_KEY);
        if (instanceProjects !== null) {
          const currentProjectId = localStorage.getItem(CURRENT_PROJECT_ID_KEY);
          const projectKeyCounter = localStorage.getItem(PROJECT_KEY_COUNTER_KEY);
          const timerType = localStorage.getItem(TIMER_TYPE_KEY);

          if (instanceProjects !== null) {
            this.projects = JSON.parse(instanceProjects);
            this.selectedProjectId = parseInt(currentProjectId);
            this.projectKeyCounter = parseInt(projectKeyCounter);
          }

          if (TIMER_TYPES.includes(timerType)) {
            this.timerType = timerType;
          }
        }
      },
      /**
       * Delete the stored instance.
       */
      discardStoredInstance() {
        localStorage.removeItem(PROJECTS_KEY);
        localStorage.removeItem(CURRENT_PROJECT_ID_KEY);
        localStorage.removeItem(PROJECT_KEY_COUNTER_KEY);
        localStorage.removeItem(TIMER_TYPE_KEY);
      },

      /**
       * Swap two elements in an array.
       * (Helper function)
       */
      swapArray(array, l, m) {
        let a = array[l];
        let b = array[m];

        // Use Vue.set() to prevent re-loading/ binding problems in v-for
        this.$set(array, l, b);
        this.$set(array, m, a);
      },
      newLine2br(str) {
        return (str) ? str.replace(/(?:\r\n|\r|\n)/g, '<br>') : "";
      }
    }
  }
</script>